// components/LandingPage.js
import React, { useState } from 'react';
import { 
  Timer, Play, Users, BarChart3, DollarSign, CheckCircle, 
  ArrowRight, Menu, X, Star, Clock, Zap, Shield, 
  Calendar, FileText, Building2, ChevronRight, Eye
} from 'lucide-react';

const LandingPage = ({ onGetStarted, onSignIn }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  // Until time tracking moves to time.logworthy.com, visitors here may be looking for the CRM. Ask once per browser.
  const [showProductChoice, setShowProductChoice] = useState(() => {
    try { return localStorage.getItem('logworthy_product_choice') !== 'time' } catch { return true }
  });

  const chooseTimeTracking = () => {
    try { localStorage.setItem('logworthy_product_choice', 'time') } catch {}
    setShowProductChoice(false);
  };

  const features = [
    {
      icon: Timer,
      title: "Smart Time Tracking",
      description: "Start, pause, and stop timers with one click. Track time across multiple projects simultaneously with our intuitive interface."
    },
    {
      icon: Users,
      title: "Team Management",
      description: "Invite team members, assign roles, and manage permissions. Perfect for agencies, consultancies, and distributed teams."
    },
    {
      icon: BarChart3,
      title: "Advanced Analytics",
      description: "Detailed reports, productivity insights, and billable hour tracking. Make data-driven decisions about your time and projects."
    },
    {
      icon: DollarSign,
      title: "Automated Billing",
      description: "Generate professional invoices automatically from tracked time. Set custom rates per project or client."
    },
    {
      icon: Building2,
      title: "Multi-Organization",
      description: "Work across multiple organizations seamlessly. Perfect for freelancers and consultants serving multiple clients."
    },
    {
      icon: Shield,
      title: "Audit Ready",
      description: "Bank-level security with role-based permissions, data encryption, and compliance features for enterprise audits."
    }
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "Creative Director",
      company: "Design Studio",
      content: "Logworthy transformed how we track project time. Our billing accuracy improved by 40% and client reporting is now effortless.",
      rating: 5
    },
    {
      name: "Marcus Rodriguez",
      role: "Consulting Partner",
      company: "Strategy Consultants",
      content: "The multi-tenant feature is perfect for our consulting firm. Each client gets their own space while we maintain oversight.",
      rating: 5
    },
    {
      name: "Emily Thompson",
      role: "Agency Owner",
      company: "Digital Marketing Agency",
      content: "Best time tracking tool we've used. The team loves it, and our project profitability has never been clearer.",
      rating: 5
    }
  ];

  const pricingPlans = [
    {
      name: "Solo",
      price: "$6.99",
      period: "per month",
      description: "Perfect for freelancers and individual consultants",
      features: [
        "Unlimited time tracking",
        "5 projects",
        "Basic reports",
        "Email support",
        "Mobile app"
      ],
      popular: false
    },
    {
      name: "Professional",
      price: "$49",
      period: "per month",
      description: "Best for small teams and growing businesses",
      features: [
        "Everything in Solo",
        "Unlimited projects",
        "Team management (up to 10 users)",
        "Advanced analytics",
        "Invoice generation",
        "Priority support",
        "API access"
      ],
      popular: true
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "pricing",
      description: "For large organizations with specific needs",
      features: [
        "Everything in Professional",
        "Unlimited users",
        "Custom integrations",
        "Advanced security",
        "Dedicated support",
        "SLA guarantee",
        "Custom branding"
      ],
      popular: false
    }
  ];

  const Navigation = () => (
    <nav className="bg-white/95 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Timer className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-slate-900">Logworthy</span>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <a href="#features" className="text-slate-600 hover:text-slate-900 transition-colors">Features</a>
            <a href="#pricing" className="text-slate-600 hover:text-slate-900 transition-colors">Pricing</a>
            <a href="#testimonials" className="text-slate-600 hover:text-slate-900 transition-colors">Reviews</a>
            <button 
              onClick={() => setShowDemo(true)}
              className="text-slate-600 hover:text-slate-900 transition-colors flex items-center space-x-1"
            >
              <Eye className="w-4 h-4" />
              <span>Demo</span>
            </button>
          </div>

          <div className="hidden md:flex items-center space-x-4">
            <button
              onClick={onSignIn}
              className="text-slate-600 hover:text-slate-900 transition-colors"
            >
              Sign In
            </button>
            <button
              onClick={onGetStarted}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-2 rounded-lg transition-all transform hover:scale-105"
            >
              Get Started Free
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-slate-600 hover:text-slate-900"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200">
            <div className="flex flex-col space-y-4">
              <a href="#features" className="text-slate-600 hover:text-slate-900 transition-colors">Features</a>
              <a href="#pricing" className="text-slate-600 hover:text-slate-900 transition-colors">Pricing</a>
              <a href="#testimonials" className="text-slate-600 hover:text-slate-900 transition-colors">Reviews</a>
              <button 
                onClick={() => setShowDemo(true)}
                className="text-slate-600 hover:text-slate-900 transition-colors flex items-center space-x-1 text-left"
              >
                <Eye className="w-4 h-4" />
                <span>Demo</span>
              </button>
              <div className="flex flex-col space-y-2 pt-4 border-t border-slate-200">
                <button
                  onClick={onSignIn}
                  className="text-slate-600 hover:text-slate-900 transition-colors text-left"
                >
                  Sign In
                </button>
                <button
                  onClick={onGetStarted}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-lg transition-all"
                >
                  Get Started Free
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );

  const Hero = () => (
    <section className="bg-gradient-to-br from-blue-50 via-white to-purple-50 py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            Time Tracking That
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> Actually Works</span>
          </h1>
          <p className="text-xl text-slate-600 mb-8 max-w-3xl mx-auto">
            Stop losing billable hours. Logworthy helps teams track time accurately, 
            manage projects efficiently, and generate professional invoices automatically.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 mb-12">
            <button
              onClick={onGetStarted}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-4 rounded-xl text-lg font-semibold transition-all transform hover:scale-105 flex items-center space-x-2 shadow-lg"
            >
              <Play className="w-5 h-5" />
              <span>Start Free Trial</span>
            </button>
            <button
              onClick={() => setShowDemo(true)}
              className="bg-white hover:bg-slate-50 text-slate-700 px-8 py-4 rounded-xl text-lg font-semibold transition-all border border-slate-200 flex items-center space-x-2"
            >
              <Eye className="w-5 h-5" />
              <span>Watch Demo</span>
            </button>
          </div>

          <div className="flex items-center justify-center space-x-8 text-slate-500 text-sm">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>14-day free trial</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>No credit card required</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>Cancel anytime</span>
            </div>
          </div>
        </div>

        {/* Hero Image/Demo */}
        <div className="mt-16 relative">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
            <div className="bg-slate-100 px-6 py-4 border-b border-slate-200 flex items-center space-x-3">
              <div className="w-3 h-3 bg-red-400 rounded-full"></div>
              <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
              <div className="w-3 h-3 bg-green-400 rounded-full"></div>
              <div className="flex-1 text-center text-slate-600 font-medium">Logworthy Dashboard</div>
            </div>
            <div className="p-8 bg-gradient-to-br from-blue-50 to-purple-50">
              <div className="text-center mb-8">
                <div className="text-6xl font-mono font-bold text-slate-800 mb-4">02:34:17</div>
                <div className="text-lg text-slate-600">Website Redesign • Acme Corp</div>
                <div className="mt-4">
                  <button className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2 mx-auto">
                    <Timer className="w-5 h-5" />
                    <span>Stop Timer</span>
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="text-2xl font-bold text-blue-600 mb-1">8.5h</div>
                  <div className="text-slate-600 text-sm">Today's Total</div>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="text-2xl font-bold text-green-600 mb-1">$1,240</div>
                  <div className="text-slate-600 text-sm">Week's Revenue</div>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="text-2xl font-bold text-purple-600 mb-1">94%</div>
                  <div className="text-slate-600 text-sm">Billable Rate</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  const Features = () => (
    <section id="features" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
            <Zap className="w-4 h-4 mr-2" />
            Powerful Features
          </div>
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Everything you need to track time like a pro
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            From simple time tracking to advanced team management and billing, 
            Logworthy has all the features you need to run your business efficiently.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div key={index} className="bg-slate-50 rounded-2xl p-8 hover:bg-white hover:shadow-lg transition-all duration-300 border border-transparent hover:border-slate-200">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mb-6">
                <feature.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">{feature.title}</h3>
              <p className="text-slate-600 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const Testimonials = () => (
    <section id="testimonials" className="py-20 bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-4">
            <Star className="w-4 h-4 mr-2" />
            Customer Reviews
          </div>
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Trusted by growing teams
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            See what our customers are saying about Logworthy
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-slate-600 mb-6 leading-relaxed">"{testimonial.content}"</p>
              <div>
                <div className="font-semibold text-slate-900">{testimonial.name}</div>
                <div className="text-slate-500 text-sm">{testimonial.role}, {testimonial.company}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const Pricing = () => (
    <section id="pricing" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-4">
            <DollarSign className="w-4 h-4 mr-2" />
            Simple Pricing
          </div>
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Choose the perfect plan for your team
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Start free and upgrade as you grow. All plans include our core time tracking features.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {pricingPlans.map((plan, index) => (
            <div key={index} className={`rounded-2xl p-8 border-2 transition-all hover:shadow-lg ${
              plan.popular 
                ? 'border-blue-500 bg-blue-50 relative' 
                : 'border-slate-200 bg-white'
            }`}>
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                    Most Popular
                  </div>
                </div>
              )}
              
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-slate-900">{plan.price}</span>
                  {plan.price !== "Custom" && <span className="text-slate-600"> / {plan.period}</span>}
                </div>
                <p className="text-slate-600">{plan.description}</p>
              </div>

              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-600">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={onGetStarted}
                className={`w-full py-3 px-6 rounded-xl font-semibold transition-all ${
                  plan.popular
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white'
                    : 'bg-slate-100 hover:bg-slate-200 text-slate-900'
                }`}
              >
                {plan.price === "Custom" ? "Contact Sales" : "Start Free Trial"}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const CTA = () => (
    <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-4xl font-bold text-white mb-6">
          Ready to transform your time tracking?
        </h2>
        <p className="text-xl text-blue-100 mb-8">
          Join growing teams who trust Logworthy to manage their time and grow their business.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
          <button
            onClick={onGetStarted}
            className="bg-white hover:bg-slate-100 text-blue-600 px-8 py-4 rounded-xl text-lg font-semibold transition-all transform hover:scale-105 flex items-center space-x-2"
          >
            <span>Start Your Free Trial</span>
            <ArrowRight className="w-5 h-5" />
          </button>
          <button
            onClick={() => setShowDemo(true)}
            className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-xl text-lg font-semibold transition-all"
          >
            Watch Demo
          </button>
        </div>
      </div>
    </section>
  );

  const Footer = () => (
    <footer className="bg-slate-900 text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Timer className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold">Logworthy</span>
            </div>
            <p className="text-slate-400 mb-6 max-w-md">
              Professional time tracking for modern teams. Track time, manage projects, 
              and grow your business with confidence.
            </p>
            <div className="text-sm text-slate-500">
              © 2025 Logworthy. All rights reserved.
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Product</h4>
            <ul className="space-y-2 text-slate-400">
              <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
              <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
              <li><button onClick={() => setShowDemo(true)} className="hover:text-white transition-colors">Demo</button></li>
              <li><a href="#" className="hover:text-white transition-colors">API</a></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Support</h4>
            <ul className="space-y-2 text-slate-400">
              <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
              <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              <li><a href="#" className="hover:text-white transition-colors">Privacy</a></li>
              <li><a href="#" className="hover:text-white transition-colors">Terms</a></li>
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );

  // Product chooser: Logworthy CRM or Logworthy Time Tracking
  const ProductChoiceModal = () => {
    if (!showProductChoice) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-labelledby="product-choice-title">
        <div className="bg-white rounded-2xl max-w-lg w-full p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-2">
            <img src="/logo192.png" alt="" className="w-8 h-8" />
            <span className="font-semibold text-slate-900">Logworthy</span>
          </div>
          <h3 id="product-choice-title" className="text-2xl font-bold text-slate-900 mb-2">What are you looking for?</h3>
          <p className="text-slate-600 mb-6">Logworthy makes two products. Choose one to continue.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <a
              href="https://crm.logworthy.com"
              className="group block rounded-xl border border-slate-200 p-5 hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <Users className="w-7 h-7 text-blue-600 mb-3" />
              <div className="font-semibold text-slate-900 flex items-center gap-1">
                Logworthy CRM <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </div>
              <p className="text-sm text-slate-600 mt-1">The CRM that keeps itself current from your customer conversations.</p>
            </a>
            <button
              onClick={chooseTimeTracking}
              className="group text-left rounded-xl border border-slate-200 p-5 hover:border-purple-500 hover:bg-purple-50 transition-colors"
            >
              <Timer className="w-7 h-7 text-purple-600 mb-3" />
              <div className="font-semibold text-slate-900 flex items-center gap-1">
                Time Tracking <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </div>
              <p className="text-sm text-slate-600 mt-1">Track time, manage teams and bill clients. Continue on this site.</p>
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Demo Modal
  const DemoModal = () => {
    if (!showDemo) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-slate-200">
            <h3 className="text-xl font-semibold text-slate-900">Logworthy Demo</h3>
            <button
              onClick={() => setShowDemo(false)}
              className="text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="p-6">
            <div className="aspect-video bg-slate-100 rounded-lg flex items-center justify-center mb-6">
              <div className="text-center">
                <Play className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600">Interactive demo coming soon!</p>
                <p className="text-sm text-slate-500 mt-2">
                  For now, click "Get Started Free" to try the full app with your own account.
                </p>
              </div>
            </div>
            <div className="flex justify-center">
              <button
                onClick={() => {
                  setShowDemo(false);
                  onGetStarted();
                }}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-lg transition-all"
              >
                Start Free Trial Instead
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <Hero />
      <Features />
      <Testimonials />
      <Pricing />
      <CTA />
      <Footer />
      <DemoModal />
      <ProductChoiceModal />
    </div>
  );
};

export default LandingPage;